import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import useNavegacionTeclado from '../../hooks/useNavegacionTeclado';

const Wrapper = styled.div`
  position: relative;
`;

// Región viva: anuncia cuántos resultados hay cada vez que cambia la búsqueda.
// Sin esto, quien usa lector de pantalla escribe y no recibe ninguna señal de
// si la lista trajo algo — el mismo final silencioso que el aviso del pie evita
// para quien ve la pantalla.
const AvisoLector = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const InputWrap = styled.div`
  position: relative;
`;

// Tamaños: 'sm' compacto (escritorio), 'md' intermedio, 'lg' táctil — este
// último con altura de 48px para que se pueda tocar con el dedo en la app
// móvil (despacho de combustible, portal de mecánicos).
const SearchInput = styled.input`
  width: 100%;
  padding: ${({ $size }) => (
    $size === 'sm' ? '0.3rem 2rem 0.3rem 0.5rem'
      : $size === 'lg' ? '12px 2rem 12px 14px'
        : '0.4rem 2rem 0.4rem 0.6rem'
  )} !important;
  border: 1px solid ${({ theme, $invalid }) => ($invalid ? theme.colors.danger : theme.colors.borderDefault)};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: ${({ $size }) => ($size === 'sm' ? '0.8rem' : $size === 'lg' ? '1rem' : '0.875rem')};
  transition-property: border-color, box-shadow;
  transition-duration: 0.2s;
  transition-timing-function: ease;
  min-height: ${({ $size }) => ($size === 'sm' ? '30px' : $size === 'lg' ? '48px' : '34px')};
  text-overflow: ellipsis;

  &:focus {
    outline: none;
    border-color: ${({ theme, $invalid }) => ($invalid ? theme.colors.danger : theme.colors.primary)};
    box-shadow: 0 0 0 3px ${({ theme, $invalid }) => ($invalid ? `${theme.colors.danger}20` : `${theme.colors.primary}20`)};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.surfaceDisabled};
    color: ${({ theme }) => theme.colors.txtSecondary};
    cursor: not-allowed;
  }
`;

const SearchIconWrap = styled.span`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.txtSecondary};
  pointer-events: none;
`;

// Botón para vaciar un filtro sin tener que entrar al campo y borrar el texto
// a mano. Solo aparece cuando hay algo elegido (`onClear` + valor no vacío) —
// en un combo obligatorio dentro de un formulario no se pasa `onClear`, así
// que ahí no cambia nada.
const ClearButton = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.colors.txtSecondary};
  cursor: pointer;
  font-size: 0.75rem;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.txtPrincipal};
  }
`;

const Suggestions = styled.div`
  position: fixed;
  z-index: ${({ theme }) => theme.zIndex.dropdownOverModal || 9500};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  max-height: 30vh;
  overflow-y: auto;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: opacity 160ms ease, transform 160ms ease;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  transform: ${(p) => (p.$open ? 'translateY(0)' : p.$dropUp ? 'translateY(6px)' : 'translateY(-6px)')};
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
  visibility: ${(p) => (p.$open ? 'visible' : 'hidden')};
`;

// $selected = es el valor guardado del campo. $activo = es la fila resaltada con
// las flechas. Son cosas distintas y se ven distinto: mientras se navega con el
// teclado, $activo tiene que ganarle tanto al valor guardado como al hover del
// mouse, porque es la única señal de qué se elige al apretar Enter.
const SuggestItem = styled.div`
  padding: ${({ $size }) => ($size === 'lg' ? '12px 14px' : '6px 8px')};
  min-height: ${({ $size }) => ($size === 'lg' ? '44px' : 'auto')};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background: ${({ $selected, $activo, theme }) => (
    $activo ? theme.colors.opcionActivaFondo
      : $selected ? theme.colors.primaryAlpha
        : theme.colors.surface
  )};
  box-shadow: ${({ $selected, $activo, theme }) => (
    $activo ? `inset 3px 0 0 ${theme.colors.opcionActivaBorde}`
      : $selected ? `inset 3px 0 0 ${theme.colors.primary}`
        : 'none'
  )};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-weight: ${({ $selected, $activo, theme }) => (
    $selected || $activo ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.regular
  )};

  &:hover {
    background: ${({ $selected, $activo, theme }) => (
      $activo ? theme.colors.opcionActivaFondo
        : $selected ? theme.colors.primaryAlpha
          : theme.colors.surfaceHover
    )};
  }
`;

const EmptyState = styled.div`
  position: fixed;
  z-index: ${({ theme }) => theme.zIndex.dropdownOverModal || 9500};
  padding: 8px 10px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.txtSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

// Pie del dropdown en modo servidor: dice si hay más resultados esperando, si
// se están trayendo, o si la búsqueda falló. Que la búsqueda falle en silencio
// es lo peor que puede pasar acá: el usuario concluye que el registro no existe
// y lo carga de nuevo duplicado.
const PieDropdown = styled.div`
  padding: 6px 10px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.txtSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
`;

// Máximo de filas dibujadas cuando el filtrado es en memoria (ver más abajo).
const MAX_FILAS_VISIBLES = 50;

const defaultGetKey = (item) => item?.id;
const defaultGetSearchText = (item) => String(item?.nombre || item?.descripcion || item?.label || '');

export default function SearchSuggestField({
  className,
  value,
  onValueChange,
  items,
  selectedKey,
  onSelectItem,
  onDoubleClickItem,
  getItemKey = defaultGetKey,
  getSearchText = defaultGetSearchText,
  renderItem,
  placeholder = 'Buscar...',
  disabled = false,
  size = 'sm',
  emptyMessage = 'Sin resultados',
  showAllOnFocus = false,
  closeOnSelect = true,
  invalid = false,
  dropUp = false,
  // El desplegable normalmente calca el ancho del input. Con autoWidth=true se
  // achica o agranda según el contenido (el ítem más largo), en vez de quedar
  // atado al ancho del campo que lo dispara — para catálogos donde el campo
  // conviene angosto pero las opciones no son todas igual de largas.
  autoWidth = false,
  // ── Búsqueda contra el servidor (opcional) ────────────────────────────────
  // Sin `onSearch`, el componente filtra en memoria sobre `items`: sirve para
  // catálogos chicos (estados, tipos, unidades), que entran enteros sin costo.
  //
  // Con `onSearch`, `items` ya viene filtrado por el padre y este componente NO
  // vuelve a filtrar. Es lo que hay que usar en catálogos que crecen sin techo
  // (repuestos, tareas, equipos, órdenes): así el formulario abre sin esperar a
  // que baje el catálogo entero, y la búsqueda igual alcanza TODAS las filas
  // —no sólo las que hubieran entrado en una primera página—, que es
  // justamente lo que se perdía cuando la paginación cortaba en 25.
  onSearch,
  hasMore = false,
  loadingMore = false,
  searchError = false,
  onLoadMore,
  // Enter sobre una opción resaltada salta al campo siguiente. Se apaga dentro
  // de una zona de alta de renglones, donde Enter significa "agregar renglón".
  saltarAlSiguiente = true,
  // Doble click sobre el propio input (no sobre una opción): lo usa el almacén
  // para abrir el detalle del registro ya elegido.
  onDoubleClickInput,
  // Muestra un botón "×" para vaciar la selección sin entrar al campo a borrar
  // el texto a mano. Pensado para filtros ("Todas las marcas"), donde no elegir
  // nada es una opción válida — no se pasa en un combo obligatorio de un
  // formulario.
  onClear,
}) {
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const clickTimerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  // Modo búsqueda: al enfocar un campo con valor ya seleccionado, el input se limpia
  // visualmente (searchTerm = '') y se despliega la lista COMPLETA, para poder buscar
  // otra opción sin tener que borrar el texto a mano. Mientras se busca, el input
  // muestra lo tipeado y filtra por eso. Al cerrar sin elegir se sale del modo búsqueda
  // y vuelve a mostrarse el valor previo (lo repone el padre vía `value`).
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const normalizedValue = value || '';
  // En reposo el input muestra el label del valor seleccionado (viene en `value`).
  // En modo búsqueda muestra el término tipeado.
  const displayValue = searching ? searchTerm : normalizedValue;

  const stopSearching = () => {
    setSearching(false);
    setSearchTerm('');
    setOpen(false);
  };

  const coincidencias = useMemo(() => {
    // Modo servidor: el padre ya devolvió lo que corresponde al término. Volver
    // a filtrar acá escondería resultados que el servidor sí encontró (por
    // ejemplo, una coincidencia en un campo que `getSearchText` no incluye).
    if (onSearch) return items || [];
    const term = (searching ? searchTerm : normalizedValue).trim().toLowerCase();
    if (!term) return showAllOnFocus ? (items || []) : [];
    return (items || []).filter((item) => getSearchText(item).toLowerCase().includes(term));
  }, [getSearchText, items, normalizedValue, showAllOnFocus, searching, searchTerm, onSearch]);

  // Tope de filas dibujadas en el filtrado en memoria. Sin él, enfocar un campo
  // sobre un catálogo grande (con showAllOnFocus, que muestra la lista entera)
  // monta miles de nodos de una y traba la pantalla justo al empezar a cargar.
  // No aplica al modo servidor: ahí el tope lo pone la paginación, y recortar de
  // nuevo acá escondería resultados que el servidor mandó a propósito.
  const hayMasSinMostrar = !onSearch && coincidencias.length > MAX_FILAS_VISIBLES;
  const filteredItems = useMemo(
    () => (onSearch ? coincidencias : coincidencias.slice(0, MAX_FILAS_VISIBLES)),
    [coincidencias, onSearch]
  );

  // Selección efectiva de una opción, sin importar si vino de un click o del
  // teclado: mismo camino para los dos, así no se pueden desincronizar.
  const seleccionarItem = (item) => {
    onSelectItem?.(item);
    setSearching(false);
    setSearchTerm('');
    if (closeOnSelect) setOpen(false);
  };

  const {
    indiceActivo,
    setIndiceActivo,
    onKeyDown,
    registrarOpcion,
    propsAriaInput,
  } = useNavegacionTeclado({
    items: filteredItems,
    abierto: open,
    setAbierto: setOpen,
    onElegir: seleccionarItem,
    onCerrar: stopSearching,
    saltarAlSiguiente,
    onCargarMas: onLoadMore,
    hayMas: hasMore,
  });

  useEffect(() => {
    if (!open || !wrapperRef.current) return;
    const updatePosition = () => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      // El root aplica `zoom` global (--app-zoom). getBoundingClientRect() devuelve
      // coordenadas ya escaladas por ese zoom, pero el dropdown se monta vía portal en
      // document.body con position:fixed, donde el zoom del root se vuelve a aplicar.
      // Para que quede pegado al input en pantallas con auto-zoom (≤1366px), pasamos las
      // coordenadas en el espacio SIN escalar dividiendo por el factor de zoom.
      const zoomRaw = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--app-zoom')
      );
      const zoom = zoomRaw > 0 ? zoomRaw : 1;
      const left = rect.left / zoom;
      const width = rect.width / zoom;
      // Sin autoWidth: el desplegable calca el ancho del input (comportamiento
      // de siempre). Con autoWidth: se le da un piso (el ancho del input, para
      // no quedar más angosto que el campo) y un techo (no se sale de la
      // pantalla), y en el medio el navegador lo ajusta solo al contenido más
      // largo vía `width: max-content`.
      const anchoStyle = autoWidth
        ? { minWidth: width, width: 'max-content', maxWidth: Math.max(width, window.innerWidth / zoom - left - 16) }
        : { width };
      if (dropUp) {
        setDropdownStyle({ left, ...anchoStyle, bottom: window.innerHeight / zoom - rect.top / zoom + 4 });
      } else {
        setDropdownStyle({ left, ...anchoStyle, top: rect.bottom / zoom + 4 });
      }
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, dropUp, autoWidth]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target) && !dropdownRef.current?.contains(event.target)) {
        stopSearching();
      }
    };

    // Escape lo maneja el onKeyDown del input (useNavegacionTeclado), no un
    // listener global: en document, el Escape de CUALQUIER campo cerraba todos
    // los combos montados a la vez y además se consumía antes de que pudiera
    // llegar al modal que contiene el formulario.
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      clearTimeout(clickTimerRef.current);
    };
  }, []);

  const showSuggestions =
    open && !disabled && (showAllOnFocus || searching || displayValue.trim().length > 0);

  // Scroll infinito: al acercarse al final del dropdown, pedir la tanda siguiente.
  const handleDropdownScroll = (event) => {
    if (!onLoadMore || !hasMore || loadingMore) return;
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
      onLoadMore();
    }
  };

  const pieDropdown = onSearch ? (
    searchError ? (
      <PieDropdown>No se pudo buscar en el servidor. Reintentá escribiendo de nuevo.</PieDropdown>
    ) : loadingMore ? (
      <PieDropdown>Buscando más resultados…</PieDropdown>
    ) : hasMore ? (
      <PieDropdown>Hay más resultados — scrolleá o refiná la búsqueda.</PieDropdown>
    ) : null
  ) : hayMasSinMostrar ? (
    // El corte se avisa siempre: una lista truncada en silencio hace creer que
    // el registro no existe y termina en un alta duplicada.
    <PieDropdown>
      {`Se muestran ${MAX_FILAS_VISIBLES} de ${coincidencias.length} — seguí escribiendo para refinar.`}
    </PieDropdown>
  ) : null;

  const dropdownContent = showSuggestions ? (
    filteredItems.length > 0 ? (
      <Suggestions ref={dropdownRef} role="listbox" $open={showSuggestions} $dropUp={dropUp} style={dropdownStyle} onScroll={handleDropdownScroll}>
        {filteredItems.map((item, indice) => {
          const itemKey = getItemKey(item);
          const selected = String(selectedKey ?? '') === String(itemKey ?? '');
          return (
            <SuggestItem
              key={itemKey}
              id={`opcion-${indice}`}
              ref={registrarOpcion(indice)}
              role="option"
              aria-selected={selected}
              $selected={selected}
              $size={size}
              $activo={indice === indiceActivo}
              // El puntero mueve el resaltado del teclado: si se pasa el mouse y
              // después se sigue con las flechas, se arranca desde donde quedó el
              // puntero y no desde donde estaba el resaltado antes.
              onMouseEnter={() => setIndiceActivo(indice)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                // Si hay onDoubleClickItem, el click simple espera un instante por si
                // llega un segundo click (doble click): sin este delay, el primer click
                // ya cierra el dropdown (closeOnSelect) y el elemento desaparece del DOM
                // antes de que el navegador pueda disparar el evento dblclick.
                if (!onDoubleClickItem) {
                  seleccionarItem(item);
                  return;
                }
                clearTimeout(clickTimerRef.current);
                // Al elegir salimos del modo búsqueda: el input pasa a mostrar el
                // label del nuevo valor (repuesto por el padre vía `value`).
                clickTimerRef.current = setTimeout(() => seleccionarItem(item), 220);
              }}
              onDoubleClick={() => {
                clearTimeout(clickTimerRef.current);
                onDoubleClickItem?.(item);
                // El doble click agrega y cierra, igual que el click simple: no debe
                // quedar el dropdown abierto tapando la lista donde cae el ítem agregado.
                setSearching(false);
                setSearchTerm('');
                setOpen(false);
              }}
            >
              {renderItem ? renderItem(item, { selected }) : <span>{getSearchText(item)}</span>}
            </SuggestItem>
          );
        })}
        {pieDropdown}
      </Suggestions>
    ) : (
      <EmptyState ref={dropdownRef} $dropUp={dropUp} style={dropdownStyle}>
        {searchError
          ? 'No se pudo buscar en el servidor. Reintentá escribiendo de nuevo.'
          : loadingMore
            ? 'Buscando…'
            : emptyMessage}
      </EmptyState>
    )
  ) : null;

  return (
    <Wrapper ref={wrapperRef} className={className}>
      <InputWrap>
        <SearchInput
          {...propsAriaInput}
          $size={size}
          $invalid={invalid}
          value={displayValue}
          disabled={disabled}
          placeholder={searching && normalizedValue ? normalizedValue : placeholder}
          autoComplete="off"
          onKeyDown={onKeyDown}
          onDoubleClick={onDoubleClickInput}
          onFocus={(event) => {
            // El autofoco automático al abrir un formulario (useAutoFocoPrimerCampo)
            // marca el campo con este atributo antes de enfocarlo: deja el cursor
            // listo para escribir pero sin desplegar la lista completa sola, que es
            // lo que se espera de un foco real (click, Tab, flecha).
            if (event.target.hasAttribute('data-suprimir-apertura-en-foco')) return;
            // Entrar en modo búsqueda: limpiar el filtro y desplegar la lista completa.
            // No se toca el valor seleccionado; si se cierra sin elegir, se restaura.
            setSearching(true);
            setSearchTerm('');
            setOpen(true);
            // Modo servidor: pedir la primera tanda para poder elegir de la lista
            // sin tipear nada.
            onSearch?.('');
          }}
          onChange={(event) => {
            const next = event.target.value;
            setSearching(true);
            setSearchTerm(next);
            setOpen(true);
            onSearch?.(next);
            // Solo avisamos al padre para limpiar la selección si el usuario borra
            // manualmente el campo. Mientras busca, el valor previo se conserva.
            if (!String(next || '').trim()) {
              onValueChange('');
            }
          }}
        />
        {onClear && normalizedValue && !searching ? (
          <ClearButton
            type="button"
            tabIndex={-1}
            title="Quitar filtro"
            onMouseDown={(event) => {
              // preventDefault: evita el blur del input antes del click, que en
              // algunos navegadores le gana la carrera al onClick.
              event.preventDefault();
            }}
            onClick={() => {
              stopSearching();
              onClear();
            }}
          >
            <i className="bi bi-x-lg" />
          </ClearButton>
        ) : (
          <SearchIconWrap><i className="bi bi-search" /></SearchIconWrap>
        )}
      </InputWrap>

      <AvisoLector role="status" aria-live="polite">
        {showSuggestions
          ? (searchError
            ? 'No se pudo buscar en el servidor.'
            : coincidencias.length === 0
              ? emptyMessage
              // Se anuncia el total encontrado, no el recortado: si no, un
              // catálogo con 300 coincidencias diría siempre "50 resultados".
              : `${coincidencias.length} ${coincidencias.length === 1 ? 'resultado' : 'resultados'}`)
          : ''}
      </AvisoLector>

      {dropdownContent ? ReactDOM.createPortal(dropdownContent, document.body) : null}
    </Wrapper>
  );
}